import React, { useEffect, useState } from "react";
import { Receipt, DollarSign, TrendingUp, Plus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Page, Card, Pill, PrimaryButton, StatCard, EmptyState } from "../components/UI";
import api from "../lib/api";
import { DatePicker } from "../components/DatePicker";
import { toast } from "sonner";

export default function Billing() {
  const [clients, setClients] = useState([]);
  const [bills, setBills] = useState([]);
  const [revenue, setRevenue] = useState({ monthly: [], total: 0, collected: 0, denial_rate: 0 });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ client_id: "", cpt_code: "90837", icd10_codes: "", modifiers: "", amount: 150, date: new Date().toISOString().slice(0,10) });

  const load = () => Promise.all([
    api.get("/clients").then(({ data }) => setClients(data)),
    api.get("/billing/sessions").then(({ data }) => setBills(data)),
    api.get("/billing/revenue").then(({ data }) => setRevenue(data)),
  ]);
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/billing/sessions", {
        client_id: form.client_id,
        cpt_code: form.cpt_code,
        icd10_codes: form.icd10_codes.split(",").map(s => s.trim()).filter(Boolean),
        modifiers: form.modifiers.split(",").map(s => s.trim()).filter(Boolean),
        amount: parseFloat(form.amount),
        date: form.date
      });
      toast.success("Session billed");
      setModal(false);
      load();
    } catch { toast.error("Billing failed"); }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/billing/sessions/${id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Page
      title="Billing & Claims"
      subtitle="AI-flagged errors, denial tracking, revenue charts."
      actions={<PrimaryButton onClick={() => setModal(true)} testid="new-bill-btn"><span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Log Session</span></PrimaryButton>}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        <StatCard label="Total Billed" value={`$${revenue.total}`} icon={DollarSign} accent="var(--mf-primary)" delay={0.05} testid="bill-total" />
        <StatCard label="Collected" value={`$${revenue.collected}`} icon={TrendingUp} accent="var(--mf-secondary)" delay={0.1} testid="bill-collected" />
        <StatCard label="Denial Rate" value={`${Math.round(revenue.denial_rate * 100)}%`} icon={Receipt} accent="#B45253" delay={0.15} testid="bill-denial-rate" />
        <StatCard label="Sessions" value={bills.length} icon={Receipt} accent="var(--mf-accent)" delay={0.2} testid="bill-sessions" />
      </div>

      <Card delay={0.25} className="mb-5" testid="revenue-chart">
        <h2 className="font-serif text-2xl mb-4">Revenue by Month</h2>
        <div className="h-64">
          {revenue.monthly.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm" style={{ color: "var(--mf-text-muted)" }}>No revenue data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={revenue.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="month" stroke="var(--mf-text-muted)" style={{ fontSize: "12px" }} />
                <YAxis stroke="var(--mf-text-muted)" style={{ fontSize: "12px" }} />
                <Tooltip contentStyle={{ background: "var(--mf-surface)", border: "none", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }} />
                <Line type="monotone" dataKey="amount" stroke="var(--mf-primary)" strokeWidth={2.5} dot={{ fill: "var(--mf-primary)", r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      <Card delay={0.3} testid="bills-list">
        <h2 className="font-serif text-2xl mb-4">Session Log</h2>
        {bills.length === 0 ? <EmptyState icon={Receipt} title="No sessions billed" description="Log your first session above." /> : (
          <div className="space-y-2">
            {bills.map((b) => (
              <div key={b.id} className="p-4 rounded-xl" style={{ background: "var(--mf-muted)" }} data-testid={`bill-item-${b.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Pill variant="primary">CPT {b.cpt_code}</Pill>
                    {b.icd10_codes.map(c => <Pill key={c}>{c}</Pill>)}
                    {b.modifiers.map(m => <Pill key={m} variant="warn">{m}</Pill>)}
                  </div>
                  <div className="font-serif text-xl" style={{ color: "var(--mf-primary)" }}>${b.amount}</div>
                </div>
                {b.ai_check && b.ai_check !== "OK" && (
                  <div className="text-xs p-2 rounded-lg mb-2" style={{ background: "rgba(201,168,76,0.15)", color: "#8a6f20" }}>
                    AI Check: {b.ai_check}
                  </div>
                )}
                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: "var(--mf-text-muted)" }}>{b.date}</span>
                  <div className="flex gap-2">
                    <button onClick={() => updateStatus(b.id, "paid")} className="text-xs px-3 py-1 rounded-full" style={{ background: "rgba(123,174,127,0.2)", color: "#4a7a4e" }} data-testid={`bill-paid-${b.id}`}>Mark Paid</button>
                    <button onClick={() => updateStatus(b.id, "denied")} className="text-xs px-3 py-1 rounded-full" style={{ background: "rgba(220,38,38,0.1)", color: "#b91c1c" }} data-testid={`bill-deny-${b.id}`}>Deny</button>
                    <Pill variant={b.status === "paid" ? "success" : b.status === "denied" ? "danger" : "warn"}>{b.status}</Pill>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setModal(false)}>
          <div className="mf-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl mb-4">Log Session</h2>
            <form onSubmit={create} className="space-y-3">
              <select required value={form.client_id} onChange={set("client_id")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-bill-client">
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="CPT code" value={form.cpt_code} onChange={set("cpt_code")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-bill-cpt" />
                <input type="number" placeholder="Amount" value={form.amount} onChange={set("amount")} className="px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-bill-amount" />
              </div>
              <input placeholder="ICD-10 codes (comma separated)" value={form.icd10_codes} onChange={set("icd10_codes")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-bill-icd" />
              <input placeholder="Modifiers (e.g. 95 for telehealth)" value={form.modifiers} onChange={set("modifiers")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-bill-modifiers" />
              <DatePicker value={form.date} onChange={set("date")} className="w-full px-4 py-2.5 rounded-xl outline-none text-sm" style={{ background: "var(--mf-muted)" }} data-testid="new-bill-date" />
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setModal(false)} className="px-4 py-2 text-sm rounded-full">Cancel</button>
                <PrimaryButton type="submit" testid="submit-new-bill">Log Session</PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </Page>
  );
}
